import React, { useState, useEffect } from 'react';
import { getHeatmap } from '../api';
import {
  MapTrifold, Factory, Drop, Anchor, Buildings, HardHat,
  CaretRight, Funnel
} from '@phosphor-icons/react';

const INDUSTRY_ICONS = {
  'Manufacturing': Factory,
  'Oil and Gas': Drop,
  'Marine': Anchor,
  'Built Environment': Buildings,
  'Materials and Construction': HardHat,
};

const METRIC_LABELS = {
  energy_mwh: 'Energy',
  tco2e: 'CO2',
  oee_pct: 'OEE',
  trir: 'TRIR',
  cost_per_unit: 'Cost',
  carbon_intensity: 'Carbon Int.',
  assurance_coverage: 'Assurance',
  scrap_pct: 'Scrap',
};

const STATUS_COLORS = {
  green: { bg: 'bg-success', text: 'text-white', light: 'bg-success/15 text-success', dot: '#2D6A4F' },
  amber: { bg: 'bg-alert-warning', text: 'text-white', light: 'bg-alert-warning/15 text-alert-warning', dot: '#F4A261' },
  red: { bg: 'bg-alert-critical', text: 'text-white', light: 'bg-alert-critical/15 text-alert-critical', dot: '#E07A5F' },
};

export default function EnterpriseHeatMap({ onSiteDrilldown }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterIndustry, setFilterIndustry] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [viewMode, setViewMode] = useState('grid');

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const { data: d } = await getHeatmap();
        setData(d.sites || []);
      } catch (err) {
        console.error('Failed to load heatmap:', err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const filtered = data.filter(s => {
    if (filterIndustry !== 'all' && s.industry !== filterIndustry) return false;
    if (filterStatus !== 'all' && s.overall !== filterStatus) return false;
    return true;
  });

  const industries = [...new Set(data.map(s => s.industry))];
  const summaryByIndustry = industries.map(ind => {
    const sites = data.filter(s => s.industry === ind);
    return {
      industry: ind,
      total: sites.length,
      green: sites.filter(s => s.overall === 'green').length,
      amber: sites.filter(s => s.overall === 'amber').length,
      red: sites.filter(s => s.overall === 'red').length,
    };
  });

  const totalGreen = data.filter(s => s.overall === 'green').length;
  const totalAmber = data.filter(s => s.overall === 'amber').length;
  const totalRed = data.filter(s => s.overall === 'red').length;

  // Get all metric keys present
  const allMetrics = ['energy_mwh', 'tco2e', 'oee_pct', 'trir', 'cost_per_unit', 'carbon_intensity', 'assurance_coverage'];

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse" data-testid="heatmap-loading">
        <div className="h-10 w-64 bg-surface rounded-lg" />
        <div className="grid grid-cols-5 gap-3">
          {[...Array(5)].map((_, i) => <div key={i} className="h-20 bg-surface border border-border rounded-lg" />)}
        </div>
        <div className="h-96 bg-surface border border-border rounded-lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="enterprise-heatmap">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-extrabold text-text-primary tracking-tight">
            Enterprise Heat Map
          </h1>
          <p className="font-body text-sm text-text-secondary mt-1">
            Cross-industry site performance — {data.length} sites across {industries.length} industries
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Funnel size={14} className="text-tertiary" />
            <select
              data-testid="heatmap-industry-filter"
              value={filterIndustry}
              onChange={(e) => setFilterIndustry(e.target.value)}
              className="px-2 py-1.5 text-xs font-body bg-background border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary"
            >
              <option value="all">All Industries</option>
              {industries.map(ind => <option key={ind} value={ind}>{ind}</option>)}
            </select>
            <select
              data-testid="heatmap-status-filter"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-2 py-1.5 text-xs font-body bg-background border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary"
            >
              <option value="all">All Statuses</option>
              <option value="green">Green</option>
              <option value="amber">Amber</option>
              <option value="red">Red</option>
            </select>
          </div>
          <div className="flex bg-background border border-border rounded-lg overflow-hidden">
            <button
              data-testid="heatmap-view-grid"
              onClick={() => setViewMode('grid')}
              className={`px-3 py-1.5 text-xs font-body font-medium transition-all ${viewMode === 'grid' ? 'bg-primary text-white' : 'text-text-secondary hover:bg-border/30'}`}
            >
              Grid
            </button>
            <button
              data-testid="heatmap-view-table"
              onClick={() => setViewMode('table')}
              className={`px-3 py-1.5 text-xs font-body font-medium transition-all ${viewMode === 'table' ? 'bg-primary text-white' : 'text-text-secondary hover:bg-border/30'}`}
            >
              Table
            </button>
          </div>
        </div>
      </div>

      {/* Summary cards by industry */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {summaryByIndustry.map((ind) => {
          const Icon = INDUSTRY_ICONS[ind.industry] || Factory;
          return (
            <button
              key={ind.industry}
              data-testid={`heatmap-summary-${ind.industry.toLowerCase().replace(/\s+/g, '-')}`}
              onClick={() => setFilterIndustry(filterIndustry === ind.industry ? 'all' : ind.industry)}
              className={`bg-surface border rounded-lg p-3 text-left transition-all hover:shadow-sm ${
                filterIndustry === ind.industry ? 'border-primary/40 ring-1 ring-primary/20' : 'border-border hover:border-tertiary/40'
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                <Icon size={16} weight="duotone" className="text-primary" />
                <span className="text-[10px] font-body font-semibold text-tertiary uppercase tracking-wider truncate">
                  {ind.industry}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-success" />
                  <span className="text-xs font-heading font-bold text-text-primary">{ind.green}</span>
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-alert-warning" />
                  <span className="text-xs font-heading font-bold text-text-primary">{ind.amber}</span>
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-alert-critical" />
                  <span className="text-xs font-heading font-bold text-text-primary">{ind.red}</span>
                </span>
              </div>
            </button>
          );
        })}
      </div>

      {/* Overall status bar */}
      <div className="bg-surface border border-border rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-body font-semibold uppercase tracking-[0.15em] text-tertiary">Enterprise Health</span>
          <span className="text-xs font-body text-text-secondary">{data.length} total sites</span>
        </div>
        <div className="flex rounded-lg overflow-hidden h-5">
          {totalGreen > 0 && (
            <div style={{ width: `${(totalGreen / data.length) * 100}%` }} className="bg-success flex items-center justify-center">
              <span className="text-[10px] font-body font-bold text-white">{totalGreen}</span>
            </div>
          )}
          {totalAmber > 0 && (
            <div style={{ width: `${(totalAmber / data.length) * 100}%` }} className="bg-alert-warning flex items-center justify-center">
              <span className="text-[10px] font-body font-bold text-white">{totalAmber}</span>
            </div>
          )}
          {totalRed > 0 && (
            <div style={{ width: `${(totalRed / data.length) * 100}%` }} className="bg-alert-critical flex items-center justify-center">
              <span className="text-[10px] font-body font-bold text-white">{totalRed}</span>
            </div>
          )}
        </div>
      </div>

      {/* Grid View */}
      {viewMode === 'grid' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="heatmap-grid">
          {filtered.map((site) => {
            const Icon = INDUSTRY_ICONS[site.industry] || Factory;
            const overallColor = STATUS_COLORS[site.overall] || STATUS_COLORS.green;
            return (
              <div
                key={`${site.industry}-${site.site}`}
                data-testid={`heatmap-site-${site.site.toLowerCase().replace(/\s+/g, '-')}`}
                className="bg-surface border border-border rounded-lg overflow-hidden hover:border-tertiary/40 hover:shadow-sm transition-all group"
              >
                {/* Site header */}
                <div className="flex items-center justify-between p-4 pb-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className={`w-3 h-3 rounded-full ${overallColor.bg}`} />
                    <div className="min-w-0">
                      <p className="font-body text-sm font-medium text-text-primary truncate">{site.site}</p>
                      <div className="flex items-center gap-1.5">
                        <Icon size={11} weight="duotone" className="text-tertiary flex-shrink-0" />
                        <span className="text-[10px] font-body text-tertiary truncate">{site.industry}</span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => onSiteDrilldown(site.industry, site.site)}
                    className="p-1.5 rounded-lg opacity-0 group-hover:opacity-100 hover:bg-background transition-all"
                    title="View details"
                  >
                    <CaretRight size={14} className="text-tertiary" />
                  </button>
                </div>

                {/* Metric cells */}
                <div className="px-4 pb-4">
                  <div className="grid grid-cols-4 gap-1.5">
                    {Object.entries(site.metrics).slice(0, 8).map(([key, m]) => {
                      const colors = STATUS_COLORS[m.status] || STATUS_COLORS.green;
                      return (
                        <div
                          key={key}
                          className={`rounded-md p-1.5 text-center ${colors.light}`}
                          title={`${METRIC_LABELS[key] || key}: ${m.value}`}
                        >
                          <p className="text-[9px] font-body font-medium truncate opacity-70">
                            {METRIC_LABELS[key] || key}
                          </p>
                          <p className="text-[11px] font-heading font-bold">
                            {typeof m.value === 'number' ? (m.value > 999 ? `${(m.value / 1000).toFixed(1)}k` : m.value) : m.value}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Footer */}
                <div className="px-4 py-2 bg-background/50 border-t border-border flex items-center justify-between">
                  <span className="text-[10px] font-body text-tertiary">{site.region}</span>
                  <span className="text-[10px] font-body text-tertiary">{site.date}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Table View */}
      {viewMode === 'table' && (
        <div className="bg-surface border border-border rounded-lg overflow-x-auto" data-testid="heatmap-table">
          <table className="w-full text-xs font-body">
            <thead>
              <tr className="border-b border-border bg-background/50">
                <th className="text-left py-3 px-4 font-semibold text-tertiary uppercase tracking-wider sticky left-0 bg-background/50 z-10">Site</th>
                <th className="text-left py-3 px-3 font-semibold text-tertiary uppercase tracking-wider">Industry</th>
                <th className="text-center py-3 px-2 font-semibold text-tertiary uppercase tracking-wider">Status</th>
                {allMetrics.map(m => (
                  <th key={m} className="text-center py-3 px-2 font-semibold text-tertiary uppercase tracking-wider whitespace-nowrap">
                    {METRIC_LABELS[m]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((site) => {
                const overallColor = STATUS_COLORS[site.overall] || STATUS_COLORS.green;
                return (
                  <tr
                    key={`${site.industry}-${site.site}`}
                    className="border-b border-border/50 hover:bg-background/30 transition-colors cursor-pointer"
                    onClick={() => onSiteDrilldown(site.industry, site.site)}
                  >
                    <td className="py-2.5 px-4 font-medium text-text-primary whitespace-nowrap sticky left-0 bg-surface z-10">
                      {site.site}
                      <span className="block text-[10px] text-tertiary font-normal">{site.region}</span>
                    </td>
                    <td className="py-2.5 px-3 text-text-secondary whitespace-nowrap">{site.industry}</td>
                    <td className="py-2.5 px-2 text-center">
                      <span className={`inline-block w-3 h-3 rounded-full ${overallColor.bg}`} />
                    </td>
                    {allMetrics.map(m => {
                      const metric = site.metrics[m];
                      if (!metric) return <td key={m} className="py-2.5 px-2 text-center text-tertiary">—</td>;
                      const colors = STATUS_COLORS[metric.status] || STATUS_COLORS.green;
                      return (
                        <td key={m} className="py-2.5 px-2 text-center">
                          <span className={`inline-flex items-center justify-center min-w-[42px] px-1.5 py-0.5 rounded-md text-[11px] font-medium ${colors.light}`}>
                            {typeof metric.value === 'number' ? (metric.value > 999 ? `${(metric.value / 1000).toFixed(1)}k` : metric.value) : metric.value}
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {filtered.length === 0 && (
        <div className="text-center py-12 bg-surface border border-border rounded-lg">
          <MapTrifold size={48} weight="duotone" className="text-border mx-auto mb-3" />
          <p className="font-body text-sm text-text-secondary">No sites match the selected filters</p>
        </div>
      )}
    </div>
  );
}
